import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { DropdownMenuSeparator } from "@arcadeai/design-system/components/ui/atoms/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import type { LucideIcon } from "lucide-react";
import { ArrowBigUp, ChevronsUpDown } from "lucide-react";
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Switcher,
  SwitcherContent,
  SwitcherInput,
  SwitcherItem,
  SwitcherTrigger,
  useSwitcher,
} from "./switcher";

type ButtonProps = ComponentProps<typeof Button>;

export type SwitcherEntity = {
  id: string;
  name: string;
  description?: string | null;
};

const MAX_SHORTCUT_NUMBER = 9;

type EntitySwitcherConfig = {
  icon: LucideIcon;
  bg: string;
  label: string;
  searchPlaceholder: string;
  emptyLabel: string;
  fallbackName: string;
  requireShiftKey: boolean;
  separateChildren: boolean;
};

export type EntitySwitcherProps = {
  entities: SwitcherEntity[];
  activeId: string;
  onSelect: (id: string) => void;
  renderItemAction?: (entity: SwitcherEntity, isSelected: boolean) => ReactNode;
  children?: ReactNode;
  /** @default true */
  enableKeyboardShortcuts?: boolean;
  triggerClassName?: string;
  triggerVariant?: ButtonProps["variant"];
  triggerSize?: ButtonProps["size"];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

// ─── EntityItemContent (internal) ────────────────────────────────────────────

function EntityItemContent({
  entity,
  config,
  isSelected,
  renderItemAction,
  enableKeyboardShortcuts,
  index,
}: {
  entity: SwitcherEntity;
  config: EntitySwitcherConfig;
  isSelected: boolean;
  renderItemAction?: (entity: SwitcherEntity, isSelected: boolean) => ReactNode;
  enableKeyboardShortcuts: boolean;
  index: number;
}) {
  const Icon = config.icon;
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleTooltipOpenChange = useCallback((nextOpen: boolean) => {
    clearTimeout(openTimerRef.current);
    if (!nextOpen) {
      setTooltipOpen(false);
      return;
    }
    openTimerRef.current = setTimeout(() => {
      const el = triggerRef.current;
      if (!el) {
        return;
      }
      const textEl = el.querySelector<HTMLDivElement>("[data-text-content]");
      if (!textEl) {
        return;
      }
      const spans = textEl.querySelectorAll("span");
      const isTruncated = Array.from(spans).some(
        (span) => span.scrollWidth > span.clientWidth
      );
      if (isTruncated) {
        setTooltipOpen(true);
      }
    }, 500);
  }, []);

  useEffect(() => () => clearTimeout(openTimerRef.current), []);

  return (
    <TooltipProvider>
      <Tooltip onOpenChange={handleTooltipOpenChange} open={tooltipOpen}>
        <TooltipTrigger
          ref={triggerRef}
          render={<div className="flex w-full min-w-0 items-center gap-2.5" />}
        >
          <div
            className={cn(
              "flex size-6 shrink-0 items-center justify-center rounded",
              config.bg
            )}
          >
            {/* color prop sets SVG stroke directly, bypassing CSS currentColor cascade */}
            <Icon className="size-3.5" color="white" />
          </div>
          <div
            className="flex min-w-0 flex-1 flex-col items-start gap-0.5"
            data-text-content
          >
            <span className="w-full truncate font-medium text-foreground text-sm leading-tight">
              {entity.name}
            </span>
            {!!entity.description && (
              <span className="w-full truncate text-muted-foreground text-xs">
                {entity.description}
              </span>
            )}
          </div>
          {renderItemAction?.(entity, isSelected)}
          {isSelected && (
            <div
              className={cn(
                "flex size-2 shrink-0 items-center justify-center self-center rounded-full",
                config.bg
              )}
            />
          )}
          {enableKeyboardShortcuts && index < MAX_SHORTCUT_NUMBER && (
            <div className="flex shrink-0 items-center gap-0.5 rounded border border-border/60 bg-muted/60 px-1.5 py-0.5 font-mono text-muted-foreground text-xs leading-none shadow-sm">
              {config.requireShiftKey && (
                <ArrowBigUp className="size-2 opacity-80" strokeWidth={2} />
              )}
              <span className="opacity-80">⌘</span>
              <span className="font-semibold text-xs">{index + 1}</span>
            </div>
          )}
        </TooltipTrigger>
        <TooltipContent
          className="max-w-xs flex-col items-start"
          side="right"
          sideOffset={8}
        >
          <span className="font-medium">{entity.name}</span>
          {!!entity.description && (
            <span className="text-background/70 text-xs">
              {entity.description}
            </span>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── EntityItems (internal) ──────────────────────────────────────────────────

function EntityItems({
  entities,
  config,
  onSelect,
  renderItemAction,
  enableKeyboardShortcuts,
}: {
  entities: SwitcherEntity[];
  config: EntitySwitcherConfig;
  onSelect: (id: string) => void;
  renderItemAction?: (entity: SwitcherEntity, isSelected: boolean) => ReactNode;
  enableKeyboardShortcuts: boolean;
}) {
  const { searchQuery } = useSwitcher();

  const hasResults =
    !searchQuery ||
    entities.some((e) =>
      `${e.name} ${e.description ?? ""}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );

  if (!hasResults) {
    return (
      <div className="py-6 text-center text-muted-foreground text-sm">
        {config.emptyLabel}
      </div>
    );
  }

  return (
    <>
      <div className="px-2 pt-2 pb-1 font-medium text-muted-foreground text-xs">
        {config.label}
      </div>
      {entities.map((entity, index) => (
        <SwitcherItem
          key={entity.id}
          onSelect={() => onSelect(entity.id)}
          textValue={`${entity.name} ${entity.description ?? ""}`}
          value={entity.id}
        >
          {({ isSelected }) => (
            <EntityItemContent
              config={config}
              enableKeyboardShortcuts={enableKeyboardShortcuts}
              entity={entity}
              index={index}
              isSelected={isSelected}
              renderItemAction={renderItemAction}
            />
          )}
        </SwitcherItem>
      ))}
    </>
  );
}

// ─── EntitySwitcher ──────────────────────────────────────────────────────────

export function EntitySwitcher({
  config,
  entities,
  activeId,
  onSelect,
  renderItemAction,
  children,
  enableKeyboardShortcuts = true,
  triggerClassName,
  triggerVariant = "outline",
  triggerSize = "sm",
  open,
  onOpenChange,
}: EntitySwitcherProps & { config: EntitySwitcherConfig }) {
  const active = entities.find((e) => e.id === activeId);
  const Icon = config.icon;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!(enableKeyboardShortcuts && (event.metaKey || event.ctrlKey))) {
        return;
      }
      const number = Number.parseInt(event.key, 10);
      if (Number.isNaN(number) || number < 1 || number > MAX_SHORTCUT_NUMBER) {
        return;
      }
      if (config.requireShiftKey ? !event.shiftKey : event.shiftKey) {
        return;
      }
      if (event.altKey || entities.length < number) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const target = entities[number - 1];
      if (target.id !== activeId) {
        onSelect(target.id);
      }
    },
    [
      enableKeyboardShortcuts,
      config.requireShiftKey,
      entities,
      activeId,
      onSelect,
    ]
  );

  useEffect(() => {
    if (!enableKeyboardShortcuts) {
      return;
    }
    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [enableKeyboardShortcuts, handleKeyDown]);

  return (
    <Switcher onOpenChange={onOpenChange} open={open} value={activeId}>
      <SwitcherTrigger>
        <Button
          className={cn(
            "h-7 gap-2 px-2.5 font-medium text-sm",
            triggerClassName
          )}
          size={triggerSize}
          variant={triggerVariant}
        >
          <div className="flex min-w-0 items-center gap-2">
            <div
              className={cn(
                "flex size-4 shrink-0 items-center justify-center rounded",
                config.bg
              )}
            >
              <Icon className="size-3" color="white" />
            </div>
            <span className="max-w-40 truncate font-medium text-foreground">
              {active?.name ?? config.fallbackName}
            </span>
          </div>
          <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </SwitcherTrigger>
      <SwitcherContent
        align="start"
        className="border-border/60 shadow-xl"
        side="bottom"
        sideOffset={6}
      >
        <SwitcherInput placeholder={config.searchPlaceholder} />
        <div className="scroll-fade max-h-[300px] overflow-y-auto overscroll-contain">
          <EntityItems
            config={config}
            enableKeyboardShortcuts={enableKeyboardShortcuts}
            entities={entities}
            onSelect={onSelect}
            renderItemAction={renderItemAction}
          />
        </div>
        {children && config.separateChildren && (
          <DropdownMenuSeparator className="my-2" />
        )}
        {children}
      </SwitcherContent>
    </Switcher>
  );
}
